import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import { cases } from '../data/cases';

// Map service IDs to case tags/categories for matching
const getRelatedCases = (serviceId) => {
  const serviceTagMap = {
    'web-development': ['WEB'],
    'mobile-app-development': ['MOBILE', 'FLUTTER'],
    'e-commerce-solutions': ['E-COMMERCE'],
    'data-science-ai': ['AI'],
    'saas': ['SAAS'],
    'api-integration': ['API'],
    'qa-testing': [],
    'software': [],
  };

  const tags = serviceTagMap[serviceId] || [];
  if (tags.length === 0) return [];

  return cases.filter(c =>
    c.tags.some(tag => tags.includes(tag)) ||
    tags.some(t => c.category.toUpperCase().includes(t))
  );
};

const AllServices = () => {
  const navigate = useNavigate();
  const { lang = 'en' } = useParams();
  const { t } = useTranslation('services');

  const serviceIds = [
    'webDevelopment',
    'mobileApp',
    'ecommerce',
    'dataScience',
    'saas',
    'apiIntegration',
    'qaTesting',
    'customSoftware',
  ];

  const serviceRouteIds = [
    'web-development',
    'mobile-app-development',
    'e-commerce-solutions',
    'data-science-ai',
    'saas',
    'api-integration',
    'qa-testing',
    'software',
  ];

  const serviceIcons = [
    <img src="/images/logos_svg/www.svg" alt="Web Development" className="w-8 h-8 invert" />,
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>,
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>,
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>,
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>,
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p className="text-white/60 text-sm font-semibold mb-4 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2 h-2 bg-white/60 rounded-full"></span>
              {t('allServices.badge')}
            </p>
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-6">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white max-w-3xl">
                {t('allServices.title')}
              </h1>
              <p className="text-base sm:text-lg text-white/60 max-w-md">
                {t('allServices.description')}
              </p>
            </div>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[280px]">
            {serviceIds.map((serviceId, index) => {
              // Define which cards are featured (larger)
              // eslint-disable-next-line no-unused-vars
              const isFeatured = index === 0 || index === 1 || index === 2;
              const isWide = index === 0; // Web Development - spans 2 cols
              const isTall = index === 1 || index === 2; // Mobile & E-commerce - spans 2 rows

              const features = t(`items.${serviceId}.features`, { returnObjects: true });

              return (
                <motion.div
                  key={serviceId}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className={`group cursor-pointer ${isWide ? 'lg:col-span-2' : ''
                    } ${isTall ? 'lg:row-span-2' : ''
                    }`}
                  onClick={() => navigate(`/${lang}/services/${serviceRouteIds[index]}`)}
                >
                  {/* Card */}
                  <div className="bg-[#111111] rounded-2xl p-8 group-hover:bg-[#1a1a1a] transition-all duration-300 relative overflow-hidden h-full flex flex-col border border-white/5 hover:border-white/10">
                    {/* Top Row: Icon and Arrow */}
                    <div className="flex justify-between items-start mb-6">
                      <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white group-hover:bg-white/15 transition-all duration-300">
                        {serviceIcons[index]}
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <svg className="w-5 h-5 text-white transform -rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                    </div>

                    {/* Bottom: Title and Description */}
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3 transition-colors duration-300">
                        {t(`items.${serviceId}.title`)}
                      </h3>
                      <p className={`text-white/50 text-sm leading-relaxed transition-colors duration-300 ${isTall ? '' : 'line-clamp-2'}`}>
                        {isTall ? t(`items.${serviceId}.description`) : t(`items.${serviceId}.description`).substring(0, 100) + '...'}
                      </p>

                      {/* Related Projects - Always show for consistent spacing */}
                      {(() => {
                        const relatedCases = getRelatedCases(serviceRouteIds[index]);
                        return (
                          <div className="mt-4 flex items-center gap-2">
                            <span className="text-white/40 text-xs">{t('allServices.usedBy')}</span>
                            <div className="flex gap-2">
                              {relatedCases.length > 0 ? (
                                relatedCases.slice(0, 2).map((c, idx) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-0.5 bg-white/10 text-white/70 text-xs font-medium rounded-full hover:bg-white/20 cursor-pointer transition-colors truncate max-w-[100px]"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/${lang}/cases/${c.id}`); }}
                                  >
                                    {c.title}
                                  </span>
                                ))
                              ) : (
                                <span className="text-white/30 text-xs">—</span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Features - only show on tall cards on desktop */}
                      {isTall && Array.isArray(features) && (
                        <div className="hidden lg:block mt-6 pt-6 border-t border-white/10">
                          <div className="flex flex-wrap gap-2">
                            {features.slice(0, 4).map((feature, idx) => (
                              <span key={idx} className="px-3 py-1 bg-white/5 text-white/60 text-xs font-medium rounded-full">
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
};

export default AllServices;